import { CIQOHLC, ExchangePeriodHoursType, isDefined, Keys } from '@eih-netbroker/common';
import { useState } from 'react';

enum MenuState {
  Collapsed = 'collapsed',
  Open = 'open',
}

export type MockedMode = {
  enabled?: boolean;
  SSO?: boolean;
};

const initialMenuState: MenuState = (() => {
  const storedMenuState = localStorage.getItem(Keys.MenuState);
  if (storedMenuState && Object.values(MenuState).includes(storedMenuState as MenuState)) {
    return storedMenuState as MenuState;
  }
  return window.innerWidth >= 1920 ? MenuState.Open : MenuState.Collapsed;
})();

export const getExchangePeriodHours = (): ExchangePeriodHoursType | undefined => {
  const exchangePeriodHours = localStorage.getItem('ExchangePeriodHours');
  return isDefined(exchangePeriodHours) ? JSON.parse(exchangePeriodHours) : undefined;
};

export const setExchangePerioidHours = (hours: ExchangePeriodHoursType | null) => {
  if (!hours) {
    localStorage.removeItem('ExchangePeriodHours');
  } else {
    localStorage.setItem('ExchangePeriodHours', JSON.stringify(hours));
  }
};

export const useLocalStorage = () => {
  const [menuState, setMenuState] = useState(initialMenuState);
  const [isDynamicSizingEnabled, setIsDynamicSizingEnabledState] = useState<boolean>(
    localStorage.getItem(Keys.DynamicSizing) === 'true',
  );
  const [mockedMode, setMockedMode] = useState<MockedMode>(
    localStorage.getItem(Keys.MockedMode) ? JSON.parse(localStorage.getItem(Keys.MockedMode) as string) : {},
  );

  const updateIsMenuCollapsed = (isCollapsed: boolean) => {
    const menuState = isCollapsed ? MenuState.Collapsed : MenuState.Open;
    setMenuState(menuState);
    localStorage.setItem(Keys.MenuState, menuState.toString());
  };

  const updateDynamicResolutionEnabled = (isDynamicSizing: boolean) => {
    setIsDynamicSizingEnabledState(isDynamicSizing);
    localStorage.setItem(Keys.DynamicSizing, isDynamicSizing.toString());
  };

  const updateMockedMode = (updatedMockedMode: Partial<MockedMode>) => {
    const result = { ...mockedMode, ...updatedMockedMode };
    setMockedMode(result);
    localStorage.setItem(Keys.MockedMode, JSON.stringify(result));
  };

  const getCachedChartData = (key: string): Array<CIQOHLC> | null => {
    try {
      const res = localStorage.getItem(`${Keys.Charts.ChartDataCachePrefix}${key}`);
      if (res) {
        return (JSON.parse(res) as Array<CIQOHLC>).map(item => ({
          ...item,
          DT: item.DT && new Date(item.DT),
        })) as Array<CIQOHLC>;
      }
    } catch (err) {
      localStorage.removeItem(`${Keys.Charts.ChartDataCachePrefix}${key}`);
    }
    return null;
  };

  const storeChartDataInCache = (key: string, data: Array<CIQOHLC>) => {
    try {
      localStorage.setItem(`${Keys.Charts.ChartDataCachePrefix}${key}`, JSON.stringify(data));
    } catch (err) {}
  };

  return {
    isMenuCollapsed: menuState === MenuState.Collapsed,
    setIsMenuCollapsed: updateIsMenuCollapsed,
    isDynamicSizingEnabled,
    setDynamicResolutionEnabled: updateDynamicResolutionEnabled,
    mockedMode,
    setMockedMode: updateMockedMode,
    getCachedChartData,
    storeChartDataInCache,
  };
};
